import { TRANSACTION_PAGE_TABS } from 'config';

/**
 * Frozen wire vocabulary for the agent activity event stream (BEAST-13036).
 *
 * Every literal the feature puts on the wire lives here and nowhere else. The AIQ backend validates
 * these strictly and per event, a violation comes back as that one event in `rejected[]` while the
 * request still answers 202, and the downstream store is immutable. So a typo here is a silent,
 * permanent data loss - never inline one of these strings at a call site, and never "fix" one
 * without checking the Java enum it mirrors:
 *   model/activity/ActivityEventType.java, ActivityEndReason.java, ActivityTargetType.java
 *   service/activity/ActivityEventEnvelopeValidator.java
 */

/** Event types accepted from the AIQ browser. Lifecycle events are backend-owned. */
export const ACTIVITY_EVENT_TYPE = Object.freeze({
    WORK_OBJECT_FOCUS_STARTED: 'work_object.focus.started',
    WORK_OBJECT_FOCUS_ENDED: 'work_object.focus.ended',
    FIELD_FOCUS_STARTED: 'field.focus.started',
    FIELD_FOCUS_ENDED: 'field.focus.ended',
    FIELD_VALUE_APPLIED: 'field.value.applied',
    FIELD_REVIEW_COMPLETED: 'field.review.completed',
    OPERATOR_STATE_CHANGED: 'operator.state.changed',
    OPERATOR_HEARTBEAT: 'operator.heartbeat',
    OPERATOR_STATE_SNAPSHOT: 'operator.state.snapshot',
    YMS_EXPORT_REQUESTED: 'yms_export.requested',
    YMS_EXPORT_RESPONDED: 'yms_export.responded',
    FACE_MATCH_REQUESTED: 'face_match.requested',
    FACE_MATCH_RESPONDED: 'face_match.responded',
});

/**
 * `target_type` carries the target on top of `event_type`, it does not replace it: all three field
 * events share `transaction_field`, so only `event_type` discriminates them. Both `yms_export.*`
 * events share `yms_export`, and the backend requires their `target_id` to equal `work_object_id`.
 */
export const ACTIVITY_TARGET_TYPE = Object.freeze({
    TRANSACTION_TAB: 'transaction_tab',
    TRANSACTION_FIELD: 'transaction_field',
    AIQ_TRANSACTION: 'aiq_transaction',
    YMS_EXPORT: 'yms_export',
    FACE_MATCH: 'face_match',
});

/**
 * The seven end reasons, all set by the browser. The backend validates the value and never infers,
 * correlates or overrides it. Note `completed_by_other`, not `completed_by_another_agent`.
 */
export const ACTIVITY_END_REASON = Object.freeze({
    CONTEXT_SWITCH: 'context_switch',
    DOCUMENT_HIDDEN: 'document_hidden',
    PAGE_UNLOAD: 'page_unload',
    COMPLETED_BY_SELF: 'completed_by_self',
    COMPLETED_BY_OTHER: 'completed_by_other',
    SESSION_EXPIRED: 'session_expired',
    UNKNOWN: 'unknown',
});

export const ACTIVITY_EVENT_CATEGORY = 'user_activity';
export const ACTIVITY_EVENT_CATEGORIES = Object.freeze({
    USER_ACTIVITY: ACTIVITY_EVENT_CATEGORY,
    WORK_OBJECT_LIFECYCLE: 'work_object_lifecycle',
    OPERATOR_STATE: 'operator_state',
});
export const ACTIVITY_WORK_OBJECT_TYPE = 'aiq_transaction';

export const ACTIVITY_FIELD_REVIEW_RESULT = Object.freeze({
    CONFIRMED: 'confirmed',
    CORRECTED: 'corrected',
    NOT_APPLICABLE: 'not_applicable',
});

/** `result` on `yms_export.responded`, one per Fenix outcome. Mirrors `YMS_EXPORT_RESULTS` in the validator. */
export const ACTIVITY_YMS_EXPORT_RESULT = Object.freeze({
    SUCCESS: 'success',
    SUCCESS_WITH_WARNING: 'success_with_warning',
    ERROR: 'error',
});

/**
 * `attempt_number` goes on the wire as a STRING ("1", "2", ...) - the shared schema's attributes are
 * string-valued. The backend accepts 1..99999 with no leading zero; the envelope omits anything else.
 */
export const ACTIVITY_MAX_YMS_EXPORT_ATTEMPT_NUMBER = 99999;

export const ACTIVITY_OPERATOR_STATE = Object.freeze({
    READY_IDLE: 'ready_idle',
    SYSTEM_WAIT: 'system_wait',
    OUTSIDE_APP: 'outside_app',
    BREAK: 'break',
    OFF_DUTY: 'off_duty',
    UNKNOWN: 'unknown',
});

export const ACTIVITY_OPERATOR_SNAPSHOT_REASON = Object.freeze({
    LOGIN: 'login',
    DESK_JOIN: 'desk_join',
    RECONNECT: 'reconnect',
    RECOVERY: 'recovery',
    UNKNOWN: 'unknown',
});

/**
 * Heartbeat interval in milliseconds. 0 disables it.
 *
 * Off by default: the heartbeat only re-sends the state we already hold, so it proves the tab is
 * open rather than that the operator is busy, and switching apps or losing connection already emit
 * their own events. Staleness is decided when the data is read, not by a periodic event.
 */
export const ACTIVITY_OPERATOR_HEARTBEAT_MS = 0;

/**
 * The shared schema declares `schema_version` as `const: 1`; the AIQ backend deliberately validates
 * only `@Min(1)` so a future v2 needs no lockstep backend release. That makes this constant the only
 * guard against sending anything but 1 - a 2 would sail through AIQ and then 400 the whole forwarded
 * batch at the shared service, long after we already had our 202. Do not compute this value.
 */
export const ACTIVITY_SCHEMA_VERSION = 1;

/** `input_source` on `field.value.applied`. Exactly these two tokens exist. */
export const ACTIVITY_INPUT_SOURCE = Object.freeze({
    MANUAL: 'manual',
    AUTOMATION: 'automation',
});

/** `visibility_state` on the two work-object focus events, read when the event is created. */
export const ACTIVITY_VISIBILITY_STATE = Object.freeze({
    VISIBLE: 'visible',
    HIDDEN: 'hidden',
});

/**
 * `page_key` - the AIQ tab context a focus period happened in, not a route or a title.
 *
 * Derived from the app's own tab ids so a tab rename cannot silently change a wire literal; the
 * unit test pins all five to their exact strings, so such a rename fails locally instead of
 * corrupting the fact store. `TRANSACTION` is the only one with no tab id of its own, because an
 * ordinary transaction tab is keyed by a numeric counter.
 */
export const ACTIVITY_PAGE_KEY = Object.freeze({
    TRANSACTION: 'transaction',
    DESKS: TRANSACTION_PAGE_TABS.DESKS_TAB.ID,
    VIDEO_WALL: TRANSACTION_PAGE_TABS.VIDEO_WALL_TAB.ID,
    REOPENED_TRANSACTION: TRANSACTION_PAGE_TABS.REOPENED_TRANSACTION_TAB.ID,
    TRANSACTIONS_SPLIT_VIEW: TRANSACTION_PAGE_TABS.TRANSACTIONS_SPLIT_VIEW_TAB.ID,
});

/**
 * `field_type` - the input component kind, from this frozen map. Never the DOM `type` attribute
 * (MUI rewrites it) and never anything derived from what the agent typed.
 */
export const ACTIVITY_FIELD_TYPE = Object.freeze({
    TEXT: 'text',
    SELECT: 'select',
    CHECKBOX: 'checkbox',
    DATE: 'date',
    AUTOCOMPLETE: 'autocomplete',
    INPUT_SELECT: 'input_select',
});

/** The `attributes` keys. `additionalProperties: false` downstream, so nothing else may appear. */
export const ACTIVITY_ATTRIBUTE_KEY = Object.freeze({
    PAGE_KEY: 'page_key',
    VISIBILITY_STATE: 'visibility_state',
    FIELD_KEY: 'field_key',
    FIELD_TYPE: 'field_type',
    INPUT_SOURCE: 'input_source',
    AUTOMATION_SOURCE: 'automation_source',
    REVIEW_RESULT: 'review_result',
    STATE: 'state',
    PREVIOUS_STATE: 'previous_state',
    STATE_REASON: 'state_reason',
    SNAPSHOT_REASON: 'snapshot_reason',
    RESULT: 'result',
    ATTEMPT_NUMBER: 'attempt_number',
    CAMERA_PAIR: 'camera_pair',
    TRIGGER: 'trigger',
    STATUS: 'status',
    CONFIDENCE_SCORE: 'confidence_score',
    CONFIDENCE_BAND: 'confidence_band',
    ERROR_TYPE: 'error_type',
    ERROR_MESSAGE: 'error_message',
});

/**
 * `face_match.*` attribute values (BEAST-13916), mirrored from the FACE_MATCH_* sets in
 * `ActivityEventEnvelopeValidator`. `input_source` here is NOT the `field.value.applied` one: the
 * backend checks it against its own two values for these two event types only.
 */
export const ACTIVITY_FACE_MATCH_CAMERA_PAIR = Object.freeze({
    CAR: 'car',
    TRUCK: 'truck',
    MIXED: 'mixed',
});

export const ACTIVITY_FACE_MATCH_INPUT_SOURCE = Object.freeze({
    LIVE_STREAMS: 'live_streams',
    SELECTED_SNAPSHOTS: 'selected_snapshots',
});

export const ACTIVITY_FACE_MATCH_TRIGGER = Object.freeze({
    MANUAL: 'manual',
    AUTOMATIC: 'automatic',
});

export const ACTIVITY_FACE_MATCH_STATUS = Object.freeze({
    SUCCESS: 'success',
    ERROR: 'error',
});

export const ACTIVITY_FACE_MATCH_CONFIDENCE_BAND = Object.freeze({
    GREEN: 'green',
    YELLOW: 'yellow',
    RED: 'red',
});

export const ACTIVITY_FACE_MATCH_ERROR_TYPE = Object.freeze({
    FACE_NOT_DETECTED: 'face_not_detected',
    LOW_IMAGE_QUALITY: 'low_image_quality',
    API_FAILURE: 'api_failure',
    TIMEOUT: 'timeout',
});

/** The backend caps `error_message` at 256 and rejects an empty one. */
export const ACTIVITY_MAX_FACE_MATCH_ERROR_MESSAGE_LENGTH = 256;

/**
 * The stable automation source id, sent only alongside `input_source = automation` and omitted for
 * manual entry. Architect decision of 2026-09-04: an optional attribute, no new schema version.
 *
 * CONFIRMED AGAINST THE BACKEND as of commit 3584467: `ActivityEventMapper` whitelists
 * `automation_source` for `field.value.applied` only, and `ActivityEventEnvelopeValidator` enforces
 * three things - the value pattern below, a 64-character cap, and that the attribute is FORBIDDEN
 * alongside `input_source = manual`. An unknown attribute key now rejects the whole event rather
 * than being dropped, so there is no longer any margin for a wrong key or a wrong value shape.
 */
export const ACTIVITY_AUTOMATION_SOURCE_UNKNOWN = 'unknown';

/**
 * The backend's value pattern for `automation_source`, mirrored from
 * `ActivityEventEnvelopeValidator.AUTOMATION_SOURCE_VALUE`. Lowercase snake_case only, which is why
 * AIQ's raw `AUTOMATION_SOURCE` values (`Auth`, `Bison`, `Manual`, ...) cannot go on the wire
 * as-is - see `ACTIVITY_AUTOMATION_SOURCE_BY_AIQ_SOURCE` in `inputSource.js`.
 */
export const ACTIVITY_AUTOMATION_SOURCE_PATTERN = /^[a-z][a-z0-9_]*$/;
export const ACTIVITY_MAX_AUTOMATION_SOURCE_LENGTH = 64;

/**
 * Client-side caps.
 *
 * The backend enforces only the event count (over 100 it answers 413 before looking at any event)
 * and the per-event 32 KB (which comes back as that event in `rejected[]`). The 512 KB batch cap is
 * no longer checked at ingest - the forwarder splits the enriched batch on its outbound leg instead -
 * but we keep ours anyway because it bounds request size. Both backend byte caps are measured on the
 * enriched envelope, which is ours plus three actor fields, so treat 32 KB as a ceiling and not as a
 * budget to fill. An oversized batch is always split, never dropped.
 */
export const ACTIVITY_MAX_EVENTS_PER_REQUEST = 100;
export const ACTIVITY_MAX_EVENT_BYTES = 32 * 1024;
export const ACTIVITY_MAX_BATCH_BYTES = 512 * 1024;

/**
 * `sendBeacon` has its own ceiling far below the batch cap (64 KB of in-flight beacons in Chromium),
 * and over it it returns false and sends nothing. It is the only transport here that fails by
 * returning a boolean rather than throwing or answering with a status.
 */
export const ACTIVITY_MAX_BEACON_BYTES = 32 * 1024;

/** A focus end queues the event; events coalesce for no longer than this before a flush. */
export const ACTIVITY_COALESCE_WINDOW_MS = 1000;

/**
 * OQ-5, decided: focus ends the instant the document becomes hidden, grace period zero.
 *
 * The asymmetry settles it - the analytical layer can always merge two adjacent periods separated by
 * a short gap, but it can never split a period we coalesced, and the store is immutable. Kept as a
 * named constant so remaining gate 1 landing on a real grace period is this line plus a timer.
 */
export const ACTIVITY_DOCUMENT_HIDDEN_GRACE_MS = 0;

/**
 * Switching to another desktop app blurs the browser window but does NOT hide the tab
 * (`document.hidden` stays false), so `visibilitychange` never fires. A window `blur` ends the open
 * tab period after this grace, and the grace absorbs a transient blur (clicking devtools and back)
 * so periods do not churn. Unlike the document-hidden case a small grace is wanted here, because a
 * window blur is far more frequent than a tab going to the background.
 */
export const ACTIVITY_WINDOW_BLUR_GRACE_MS = 800;

/**
 * Plain action the lifecycle layer dispatches when the window regains focus, so the saga can reopen
 * the tab period for the current active transaction (the DOM listener has no store access to it).
 */
export const ACTIVITY_WINDOW_FOCUSED_ACTION = 'activityTracking/windowFocused';

/**
 * Plain action for an Agent's value change made OUTSIDE the field itself (the step-by-step protocol's
 * Detected select writes `vehicle.transportType`), payload `{ formId, fieldName }`. Such a change has
 * no focus period on the field, so the blur that reports a manual edit never comes; the saga emits the
 * manual `field.value.applied` on occurrence instead.
 */
export const ACTIVITY_EXTERNAL_MANUAL_VALUE_APPLIED_ACTION = 'activityTracking/externalManualValueApplied';

/**
 * Bounded retry: THREE RETRIES after the first send, delayed by these values with jitter, and
 * honouring `Retry-After` when the response carries one. Four transmissions at most.
 *
 * One entry per retry, so the array length IS the retry bound - `sendChunk` gives up at
 * `attempt >= length`. Shortening the array shortens the bound; there is no separate count to keep
 * in step.
 */
export const ACTIVITY_RETRY_DELAYS_MS = Object.freeze([1000, 5000, 20000]);
export const ACTIVITY_RETRY_JITTER_RATIO = 0.25;

/**
 * Transient: retry the whole batch unchanged. `503` is the only one the AIQ controller itself
 * returns and it means "I could not durably accept this" (the queue publish failed), so retrying is
 * exactly right. The other three can only come from the edge (nginx / ALB / WAF), so their body will
 * not be our JSON - never parse a body from them.
 */
export const ACTIVITY_RETRYABLE_STATUSES = Object.freeze([429, 502, 503, 504]);

/**
 * Permanent, drop and log. `400` means the body was not a valid batch at all; `413` means more than
 * 100 events and nothing else, so it is our bug, and it may be an HTML page from Tomcat rather than
 * our JSON.
 */
export const ACTIVITY_DROP_STATUSES = Object.freeze([400, 413]);

/**
 * Permanent, and they stop the queue for the lifetime of the page. Inverted from what you would
 * expect: `401` is a valid token with the wrong role (`@PreAuthorize` denies and a custom
 * `AccessDeniedHandler` answers 401, and `AgentActivityService.resolveActor` answers it too when the
 * principal is not a `UserAuthDTO`), while `403` is the missing, expired or invalid token, because
 * the Keycloak filter never sends 401 and never redirects - it continues anonymously and Spring's
 * default entry point answers 403. Both are permanent, so the inversion changes no behaviour; it
 * matters only when reading logs. No closing events are synthesized after either.
 */
export const ACTIVITY_STOP_STATUSES = Object.freeze([401, 403]);

/**
 * The shared schema's pattern for `field_key`, `page_key` and `field_type`. Letters, digits,
 * underscore, dot, colon and hyphen only - no spaces, no slashes, no brackets, no empty string.
 * The form config is server-driven, so the full set of field keys is not statically knowable and one
 * bad key would fail the shared schema and destroy the whole batch it landed in.
 */
export const ACTIVITY_SCHEMA_KEY_PATTERN = /^[a-zA-Z0-9_.:-]+$/;
export const ACTIVITY_MAX_SCHEMA_KEY_LENGTH = 128;
export const ACTIVITY_MAX_FIELD_TYPE_LENGTH = 64;

/** Prefix for a `field_key` the pattern guard had to rewrite, so it is recognizable in a query. */
export const ACTIVITY_SANITIZED_FIELD_KEY_PREFIX = 'fk-';

/** `POST` target. Nothing outside the activity event service may call it. */
export const ACTIVITY_EVENTS_ENDPOINT = '/activity/events';

/**
 * The 19 envelope keys the browser owns, in serialization order.
 *
 * All 19 are emitted on every event, unconditionally, with an explicit `null` where there is no
 * value. Three more (`actor_id`, `identity_namespace`, `actor_type`) are stamped by the AIQ backend
 * and two (`source_system`, `ingested_at`) by the shared service - we must never send any of those
 * five. Never add a key either: not a debug field, not a client version. The AIQ backend would drop
 * it silently, which is a bug you cannot find by reading the response.
 */
export const ACTIVITY_ENVELOPE_KEYS = Object.freeze([
    'event_id',
    'event_type',
    'event_category',
    'schema_version',
    'occurred_at',
    'producer_session_id',
    'producer_sequence',
    'work_object_type',
    'work_object_id',
    'target_type',
    'target_id',
    'correlation_id',
    'causation_id',
    'end_reason',
    'attributes',
    'location_id',
    'gate_id',
    'lane_id',
    'desk_id',
]);
